import { useCallback } from 'react';

import { DragOverlay } from '@dnd-kit/react';

import { DealCard } from '@crm/entities/deal';
import { getSourceDealId } from '@crm/pages/deals/lib';

import type { TDeal } from '@crm/entities/deal';
import type { TDealColumn } from '@crm/entities/deals-board';
import type { DropAnimationFunction } from '@dnd-kit/dom';

type TDealDragOverlayProps = {
    columns: TDealColumn[];
    onDragCleanup: () => void;
};

const findDealById = (columns: TDealColumn[], dealId: TDeal['id']) =>
    columns.reduce<TDeal | undefined>(
        (foundDeal, column) =>
            foundDeal ?? column.deals.find((currentDeal) => currentDeal.id === dealId),
        undefined
    );

export const DealDragOverlay = ({ columns, onDragCleanup }: TDealDragOverlayProps) => {
    const handleDropAnimation = useCallback<DropAnimationFunction>(
        ({ feedbackElement, source }) => {
            if (source.type !== 'deal') {
                return Promise.resolve();
            }

            feedbackElement.setAttribute('hidden', '');
            onDragCleanup();

            return new Promise<void>((resolve) => {
                requestAnimationFrame(() => {
                    feedbackElement.removeAttribute('hidden');
                    resolve();
                });
            });
        },
        [onDragCleanup]
    );

    return (
        <DragOverlay
            className="pointer-events-none fixed z-[100] m-0 border-0 bg-transparent p-0 opacity-50 outline-none [&:not([data-dnd-dragging])]:hidden"
            disabled={(source) => source?.type !== 'deal'}
            dropAnimation={handleDropAnimation}
            style={{ overflow: 'hidden' }}
            tag="div"
        >
            {(source) => {
                const sourceDealId = getSourceDealId(source);

                if (sourceDealId === undefined) {
                    return null;
                }

                const dealId = Number(sourceDealId);

                if (!Number.isFinite(dealId)) {
                    return null;
                }

                const deal = findDealById(columns, dealId);

                if (!deal) {
                    return null;
                }

                return <DealCard dealCardData={deal} />;
            }}
        </DragOverlay>
    );
};
